<template>
  <div v-if="event !== undefined" :style="{ background: event.branding.tertiaryColor }">
    <div class="bg-layer" :style="{ background: event.branding.tertiaryColor }" />
    <div class="card card-overlay">
      <b-steps v-model="onboardStep" type="is-primary" :has-navigation="false">
        <b-step-item v-if="!isHybridUser" :label="$t('welcome')" :clickable="false">
          <h1 class="title is-3 mb-1 mt-2">
            <span v-if="event.name">{{ event.name }}</span>
            <b-skeleton v-else height="36px" />
          </h1>
          <!--
          <div v-if="organizerUserInfo && organizerUserInfo.firstName && organizerUserInfo.lastName">
            <b-icon
              icon="account"
              style="vertical-align:middle"
            />
            <span
              class="ml-2 has-text-weight-semibold is-size-5 is-light"
              style="vertical-align:middle"
            >{{ organizerUserInfo.firstName }} {{ organizerUserInfo.lastName }}</span>
          </div>
          <b-skeleton
            v-else
            height="30px"
            width="20%"
          />-->
          <div v-if="hasCode && invalidCode">
            Code
            <b-input v-model="intcode" type="password" :loading="loadingIndicator" :disabled="loadingIndicator" />
            <br />
            <b-button class="is-primary" @click="enterCode"> Login </b-button>
          </div>

          <div v-if="(hasCode && !invalidCode) || !hasCode">
            <b-field v-if="!isTranslator && langWhiteList.length > 0" :label="$t('interpretationIn')" class="mt-3">
              <LanguagePicker
                v-model="consumingLang"
                :whitelist="langWhiteList"
                :change-local="true"
                :loading="loadingIndicator"
                :disabled="loadingIndicator"
              />
            </b-field>
          </div>
          <b-field v-if="isTranslator" id="tt-step-1" :label="$t('iTranslateFrom')" class="mt-3">
            <LanguagePicker
              v-model="clientTranslator.lang1"
              :whitelist="langWhiteList"
              :blacklist="[clientTranslator.lang2]"
              :change-local="false"
              :loading="loadingIndicator"
              :disabled="loadingIndicator"
            />
          </b-field>
          <b-field v-if="isTranslator" :label="$t('iTranslateTo')" class="mt-3">
            <LanguagePicker
              v-model="clientTranslator.lang2"
              :whitelist="langWhiteList"
              :blacklist="[clientTranslator.lang1]"
              :change-local="false"
              :loading="loadingIndicator"
              :disabled="loadingIndicator"
            />
          </b-field>
          <div v-if="isTranslator && false" class="field mt-3">
            <b-switch v-model="showRelay">
              {{ $t("optRelaySound") }}
            </b-switch>
          </div>

          <b-field v-if="isTranslator && showRelay" :label="$t('relay')" class="mt-3">
            <LanguagePicker
              :whitelist="langWhiteList"
              :change-local="false"
              :loading="loadingIndicator"
              :disabled="loadingIndicator"
              :multi-select="true"
              :lang-list="settingsRelayLang"
              @multiSelect="
                (l) => {
                  settingsRelayLang = l;
                }
              "
            />
          </b-field>
          <div v-if="(hasCode && !invalidCode) || !hasCode">
            <b-field :label="$t('nickname')">
              <b-input
                id="name-input"
                v-model="nickname"
                :lazy="true"
                :loading="loadingIndicator"
                :disabled="loadingIndicator"
                @blur="setNickname"
              />
            </b-field>
            <b-button
              type="is-primary"
              class="mr-2"
              :loading="loadingIndicator"
              :disabled="
                (!isTranslator && producingLang !== 'any' && langWhiteList.length > 0) ||
                (!isTranslator && (!langWhiteList.concat(['any']).includes(producingLang) || nickname == '')) ||
                (isTranslator &&
                  (!langWhiteList.includes(clientTranslator.lang1) ||
                    !langWhiteList.includes(clientTranslator.lang2) ||
                    nickname == ''))
              "
              @click="startJoin"
            >
              {{ isTranslator ? $t("joinAsTranslator") : $t("joinAsSpeaker") }}
            </b-button>
          </div>
        </b-step-item>

        <b-step-item :label="$t('chooseDevices')">
          <div class="device-settings-container">
            <div class="local-video-test">
              <div ref="localvideotest" style="height: calc(100% - 1em - 60px)" />
              <av-media
                :media="audioMedia"
                :type="'frequ'"
                :canv-height="60"
                :line-color="event.branding.primaryColor"
                :frequ-line-cap="true"
                :line-width="0.8"
              />
            </div>
            <div class="device-settings">
              <div v-if="localAudioTrack">
                <b-field :label="$t('audio')">
                  <b-tooltip :label="$t('audioSelectDesc')" position="is-right" multilined animated>
                    <b-select
                      v-model="currentMicId"
                      placeholder="Select a device"
                      :loading="loadingIndicator"
                      :disabled="loadingIndicator"
                      @input="setupMicTest"
                    >
                      <option v-for="mic in microphones" :key="mic.deviceId" :value="mic.deviceId">
                        {{ mic.label }}
                      </option>
                    </b-select>
                  </b-tooltip>
                </b-field>
              </div>
              <div class="mt-3">
                <b-field :label="$t('video')">
                  <b-tooltip :label="$t('cameraSelectDesc')" position="is-right" multilined animated>
                    <b-select
                      v-model="currentCamId"
                      placeholder="Select a device"
                      :loading="loadingIndicator"
                      :disabled="loadingIndicator"
                      @input="setupCamTest(currentCamId)"
                    >
                      <option v-for="cam in cameras" :key="cam.deviceId" :value="cam.deviceId">
                        {{ cam.label }}
                      </option>
                    </b-select>
                  </b-tooltip>
                </b-field>
              </div>
              <div class="mt-3">
                <b-field grouped>
                  <b-button
                    class="mr-2"
                    :icon-right="localMute ? 'microphone-off' : 'microphone'"
                    :type="localMute ? 'is-danger is-light' : ''"
                    @click="toggleMute"
                  />
                  <b-button
                    v-if="!isTranslator"
                    class="mr-2"
                    :icon-right="localVideoMute ? 'video-off' : 'video'"
                    :type="localVideoMute ? 'is-danger is-light' : ''"
                    @click="toggleVideoMute"
                  />
                  <b-button
                    v-if="!isTranslator"
                    icon-right="translate-off"
                    :type="noTranslation ? 'is-danger is-light' : ''"
                    @click="noTranslationSelected"
                  >
                    {{ $t("noTranslationBtnLabel") }}
                  </b-button>
                </b-field>
              </div>
              <b-button type="is-primary" class="mt-3" :loading="loadingIndicator" @click="connectToRoom">
                {{ $t("joinEvent") }}
              </b-button>
            </div>
          </div>
          <b-loading :is-full-page="false" :active="loadingIndicator" />
        </b-step-item>
      </b-steps>
    </div>
  </div>
</template>
<script>
/**
 * Handles the onboarding process of the user:
 * Language selection, Displayname set, mic and video selection
 * @displayName Onboarding
 */
import LanguagePicker from "../LanguagePicker.vue";
/* eslint-disable no-unused-vars*/
import { auth, db, functions, FieldValue } from "@/firebase";
import { detect } from "detect-browser";
import { mapState } from "vuex";

export default {
  name: "Onboarding",
  components: {
    LanguagePicker,
  },
  props: {
    /**
     * information about the event:
     * Backgroundcolor, Lang combs, etc
     */
    event: Object,
    /**
     * Whether User is a translator
     */
    isTranslator: Boolean,
    /**
     * Whether User is a hybrid User
     */
    isHybridUser: Boolean,
  },

  data() {
    return {
      onboardStep: 0,
      loadingIndicator: false,
      consumingLang: "xxx",
      producingLang: "any",
      nickname: "",
      clientTranslator: {
        lang1: null,
        lang2: null,
        currentOutputLang: null,
        bidirectional: false,
        requestHandover: false,
        translatorReady: false,
      },
      showRelay: false,
      localVideoTrack: true,
      localAudioTrack: true,
      localVolumeLevel: 0.1,
      localVolumeLevelBuffer: [0],
      currentCamId: null,
      currentMicId: null,
      audioTestElem: null,
      videoTestElem: null,
      audioMedia: null,
      currentStream: null,
      audioConstraints: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    };
  },
  computed: {
    ...mapState({
      mediasoupId: (state) => state.mediasoup.mediasoupId,
      microphones: (state) => state.mediasoup.microphones,
      cameras: (state) => state.mediasoup.cameras,
      localMute: (state) => state.mediasoup.micPaused,
      localVideoMute: (state) => state.mediasoup.vidPaused,
      noTranslation: (state) => state.mediasoup.noTranslation,
      role: (state) => state.mediasoup.role,
    }),
    hasCode: {
      get() {
        return this.event.code !== null && this.event.code !== undefined;
      },
    },
    invalidCode: {
      get() {
        return this.event.code !== this.code;
      },
    },
    langWhiteList() {
      return this.event.langComb
        ? this.event.langComb
            .map((lc) => [lc.lang1, lc.lang2])
            .flat()
            .filter((l, i, arr) => arr.indexOf(l) == i)
        : [];
    },

    participantsCollection() {
      return db.collection(
        `organizations/${this.$route.params.organizationId}/events/${this.$route.params.eventId}/participants`,
      );
    },
    translatorsCollection() {
      return db.collection(
        `organizations/${this.$route.params.organizationId}/events/${this.$route.params.eventId}/translators`,
      );
    },
  },
  async created() {
    if (this.role !== "proxy") {
      //console.log("ONBOARDING CREATET ROLE", this.role);
      this.browserCheck();
    }

    if (this.$route.query.role === "hybrid") {
      await this.startJoin();
    }

    try {
      var deviceLang = navigator.language || navigator.userLanguage;
      let browser = detect();

      this.$store.commit("mediasoup/set_browserName", browser.name);

      if (deviceLang && this.$i18n.availableLocales.includes(deviceLang.substr(0, 2))) {
        this.$i18n.locale = deviceLang.substr(0, 2);
      } else {
        this.$i18n.locale = "en";
      }
    } catch (error) {
      console.warn(error);
    }
  },
  methods: {
    /**
     * Checks if the current Browser is supported
     * @public
     */
    async browserCheck() {
      try {
        let browser = detect();
        if (browser.name == "chrome" && parseFloat(browser.version.split(".")[0]) > 69) {
          console.log("Browser Chrome Okay");
        } else if (browser.name == "firefox" && parseFloat(browser.version.split(".")[0]) > 60) {
          console.log("Browser Firefox Okay");
        } else if (browser.name == "ios" && parseFloat(browser.version.split(".")[0]) >= 11) {
          console.log("Browser Ios Okay");
        } else if (
          browser.name == "safari" &&
          navigator.maxTouchPoints > 2 &&
          parseFloat(browser.version.split(".")[0]) >= 11
        ) {
          console.log("Browser iPadOs Okay");
        } else if (browser.name == "safari" && parseFloat(browser.version.split(".")[0]) >= 11) {
          console.log("Browser Safari Okay");
        } else if (browser.name == "safari" && parseFloat(browser.version.split(".")[0]) >= 11) {
          console.log("Browser Safari Okay");
        } else if (browser.name == "edge-chromium" || browser.name == "Chrome") {
          console.log("Browser Edge on Chromium Okay");
        } else {
          console.log("Unsupported Browser", browser);
          // window.location.replace(
          //   "https://goqonda.io/unsupported-browser?" +
          //     encodeURIComponent(JSON.stringify({ ...browser }))
          // );
        }
      } catch (err) {
        console.error(err);
      }
    },
    /**
     * get the media devices (mic/webcam) of the users browser
     * @public
     */
    async getDevices() {
      let videoConstraints = {
        width: { min: 320, ideal: 1080, max: 1920 },
        height: { min: 240, ideal: 720, max: 1080 },
        aspectRatio: { ideal: 1.7777777778 },
      };
      console.log("video constraints", videoConstraints);
      try {
        await navigator.mediaDevices
          .getUserMedia({
            audio: this.audioConstraints,
            video: videoConstraints,
          })
          .then(async (stream) => {
            const available = await navigator.mediaDevices.enumerateDevices();
            let cameras = [];
            let microphones = [];
            for (const device of available) {
              if (device.kind === "videoinput") cameras.push(device);
              if (device.kind === "audioinput") microphones.push(device);
            }
            this.$store.commit("mediasoup/set_microphones", microphones);
            this.$store.commit("mediasoup/set_cameras", cameras);
            stream.getTracks().forEach((t) => t.stop()); // stops the tracks
          });
      } catch (error) {
        console.error("File: Onboarding.vue, Error getting devices:", error);
      }
    },
    toggleMute() {
      this.$store.commit("mediasoup/toggle_mute");
    },
    toggleVideoMute() {
      this.$store.commit("mediasoup/toggle_video_mute");
    },
    setNickname() {
      this.$store.commit("mediasoup/set_displayName", this.nickname);
    },
    /**
     * starts joining process calls joinChannel and initDeviceConfig
     * @public
     */
    async startJoin() {
      // start loading animation
      this.loadingIndicator = true;
      this.$store.commit("mediasoup/set_clientTranslator", this.clientTranslator);
      await this.initDeviceConfig();
      await this.joinChannel();

      this.loadingIndicator = false;
    },
    /**
     * Sign in to firebase, set mediasoup Id, store the user in firebase
     * @public
     */
    async joinChannel() {
      if (this.$store.getters.getCurrentUser == null) {
        await auth.signInAnonymously();
      }

      let mediasoupId = String(Math.random() * 10000);
      // storing user related values to firebase and vuex
      if (this.isTranslator) {
        await this.participantsCollection.doc(this.$store.getters.getCurrentUser.uid).delete();
        this.clientTranslator.currentOutputLang = this.clientTranslator.lang2;
        await this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
          {
            name: this.nickname,
            mediasoupId: mediasoupId,
            lang1: this.clientTranslator.lang1,
            lang2: this.clientTranslator.lang2,
            currentOutputLang: this.clientTranslator.lang2,
            ready: false,
            lastActive: FieldValue.serverTimestamp(),
            willTake: false,
            requestHandover: false,
            onAir: false,
          },
          { merge: true },
        );

        this.$store.commit("mediasoup/set_produce_lang", this.clientTranslator.currentOutputLang.toUpperCase());
        this.$store.commit("mediasoup/set_consumeLang", this.clientTranslator.lang1.toUpperCase());
        this.$store.commit("mediasoup/set_mediasoupId", mediasoupId);
      }
      // storing hybrid user
      else if (this.isHybridUser) {
        await this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).delete();
        await this.participantsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
          {
            name: "Hybrid User",
            lang: this.consumingLang,
            mediasoupId: this.mediasoupId,
            lastActive: FieldValue.serverTimestamp(),
            isSupport: Object.keys(this.$route.query).includes("support"),
            reqSwitchToHost: false,
            isAllowedToSpeak: false,
          },
          { merge: true },
        );
      } else {
        await this.translatorsCollection.doc(this.$store.getters.getCurrentUser.uid).delete();
        await this.participantsCollection.doc(this.$store.getters.getCurrentUser.uid).set(
          {
            name: this.nickname,
            lang: this.consumingLang,
            mediasoupId: mediasoupId,
            lastActive: FieldValue.serverTimestamp(),
            isSupport: Object.keys(this.$route.query).includes("support"),
            reqSwitchToHost: false,
            isAllowedToSpeak: false,
          },
          { merge: true },
        );
        this.$store.commit("mediasoup/set_produce_lang", this.producingLang.toUpperCase());
        this.$store.commit("mediasoup/set_consumeLang", this.consumingLang.toUpperCase());
        this.$store.commit("mediasoup/set_mediasoupId", mediasoupId);
      }

      this.$emit("update-active");

      // generate a Token to join the channel
      let { data } = await functions.httpsCallable("GenerateToken")({
        eventId: this.$route.params.eventId,
        organizationId: this.$route.params.organizationId,
        eventPw: this.$route.params.eventPw,
      });
    },
    /**
     * gets Users devices and proceeds to the next onboarding step
     * @public
     */
    async initDeviceConfig() {
      this.getDevices();
      this.onboardStep = 1;
    },
    /**
     * creates and inits video tile to test camera
     * @public
     */
    async setupCamTest(currentCamId) {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: currentCamId,
          width: { min: 320, ideal: 1920, max: 1920 },
          height: { min: 240, ideal: 1080, max: 1080 },
          aspectRatio: { ideal: 1.7777777778 },
        },
      });

      this.currentStream = stream;

      if (this.videoTestElem) {
        this.videoTestElem.pause();
        this.videoTestElem.srcObject = stream;
        this.videoTestElem.play().catch((error) => console.log("videoElem.play() failed:%o", error));
      } else {
        this.videoTestElem = document.createElement("video");
        this.videoTestElem.srcObject = stream;
        this.videoTestElem.play().catch((error) => console.log("videoElem.play() failed:%o", error));
        this.$refs.localvideotest.appendChild(this.videoTestElem);
      }

      this.localVideoTrack = stream.getVideoTracks()[0];
      this.$store.commit("mediasoup/set_cam_id", this.currentCamId);
    },
    /**
     * creates and inits the audio playback in the selection
     * @public
     */
    async setupMicTest() {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { ideal: this.currentMicId },
          ...this.audioConstraints,
        },
      });

      this.audioMedia = stream;

      if (this.audioTestElem) {
        this.audioTestElem.pause();
        this.audioTestElem.srcObject = stream;
        this.audioTestElem.play().catch((error) => console.log("audioElem.play() failed:%o", error));
      } else {
        this.audioTestElem = document.createElement("audio");
        this.audioTestElem.srcObject = stream;
        this.audioTestElem.play().catch((error) => console.log("audioElem.play() failed:%o", error));
        this.$refs.localvideotest.appendChild(this.audioTestElem);
      }

      this.localAudioTrack = stream.getAudioTracks()[0];
      console.log(this.localAudioTrack);

      this.$store.commit("mediasoup/set_mic_id", this.currentMicId);
    },
    /**
     * Sets onboarding to false in vuex store.
     * which then renders the Room.vue component
     * in MediasoupEvent.vue component
     */
    connectToRoom() {
      //stopping the testCam video tracks
      if (this.currentStream) {
        this.currentStream.getVideoTracks().forEach((t) => t.stop());
      }
      this.$store.commit("mediasoup/onboardingComplete");
    },
    /**
     * Sets noTranslation variable in vuex store
     * to true
     */
    noTranslationSelected() {
      if (!this.noTranslation) {
        this.$store.commit("mediasoup/set_noTranslation", true);
      } else {
        this.$store.commit("mediasoup/set_noTranslation", false);
      }
    },
  },
};
</script>
